import { browserHistory } from 'react-router';
import FormData from 'form-data';

import { openOverlay } from 'app/shared/modules/overlay/actions';
import * as Config from 'app/config';
import { apiCall } from 'app/dealer/actions';
import { resetAuthorities } from 'app/dealer/shared/modules/authorities/actions';
import { resetBranch } from 'app/dealer/account/Branch/actions';
import { resetBranches } from 'app/dealer/account/Branches/actions';
import { resetNetwork } from 'app/dealer/account/Network/actions';
import { resetListings } from 'app/dealer/listings/actions';
import { resetPaymentMethod } from 'app/dealer/account/PaymentMethod/actions';
import { tokenNearExpiry } from 'app/shared/utils/tokenNearExpiry';
import { ConnectedTokenWarning as TokenWarning } from 'app/dealer/shared/components/TokenWarning/ConnectedTokenWarning';

import * as ActionTypes from './constants';

export const confirmLeadPlan = () => {
  return dispatch => {
    return dispatch({ type: ActionTypes.LEAD_PLAN_SELECT });
  };
};

export const logout = (shouldRedirect = true) => {
  return dispatch => {
    const redirectTo = '/dealer/request-invite';

    return Promise.all([
      dispatch({ type: ActionTypes.LOGOUT_REQUEST }),
      dispatch(resetBranch()),
      dispatch(resetBranches()),
      dispatch(resetListings()),
      dispatch(resetNetwork()),
      dispatch(resetPaymentMethod()),
      dispatch(resetAuthorities()),
    ])
      .then(() => {
        dispatch({ type: ActionTypes.LOGOUT_SUCCESS });
        return shouldRedirect && browserHistory.push(redirectTo); // Logout but do NOT redirect
      })
      .catch(error => {
        return dispatch({ type: ActionTypes.LOGOUT_FAILURE, error });
      });
  };
};

export const revokeToken = () => {
  return dispatch => {
    const url = '/uaa/oauth/revokeToken';
    const options = {
      method: 'POST',
    };

    return dispatch(apiCall(url, options)).then(() => {
      return dispatch(logout(true));
    });
  };
};

const openLogoutOverlay = dispatch => {
  dispatch(
    openOverlay(TokenWarning, {
      props: {
        modalId: 'token-warning',
      },
    }),
  );
};

const trackingIsDisabled = username =>
  /@hey.car|@mobility-trader.de/.test(username);

const authenticate = (dispatch, form) => {
  const formData = new FormData();

  dispatch({ type: ActionTypes.GET_AUTH_REQUEST });
  formData.append('username', form.username);
  formData.append('password', form.password);
  formData.append('grant_type', 'password');
  const url = '/uaa/oauth/token';
  const options = {
    method: 'POST',
    body: formData,
    headers: { Authorization: 'Basic d2ViLWFwcDo=' },
  };

  const trackingDisabled = trackingIsDisabled(form.username);

  return dispatch(apiCall(url, options))
    .then(response => {
      return dispatch({
        type: ActionTypes.GET_AUTH_SUCCESS,
        payload: { ...response, trackingDisabled },
      });
    })
    .catch(error => {
      dispatch({ type: ActionTypes.GET_AUTH_FAILURE, error });

      return Promise.reject(error);
    });
};

export const authenticateUser = form => {
  return (dispatch, getState) => {
    if (getState().dealerLogin.userLoggedIn) {
      return dispatch(logout()).then(() => {
        return authenticate(dispatch, form);
      });
    }
    return authenticate(dispatch, form);
  };
};

export const refreshToken = () => {
  return (dispatch, getState) => {
    const { dealerLogin } = getState();
    const {
      accessToken,
      authResponse,
      expires,
      pending,
      userId,
      userLoggedIn,
      refreshToken: refreshTokenString,
    } = dealerLogin;
    const tokenWillExpireShortly = tokenNearExpiry(expires);

    if (
      (userLoggedIn || authResponse === 'ok') &&
      ((accessToken === '""' && userId === '""') ||
        (accessToken === '' && userId === ''))
    ) {
      dispatch({ type: ActionTypes.AUTH_RESET });

      return Promise.resolve();
    }

    if (accessToken !== '' && tokenWillExpireShortly && !pending) {
      const formData = new FormData();
      const url = `${Config.COMMON_URL}/uaa/oauth/token`;
      const options = {
        method: 'POST',
        body: formData,
        headers: { Authorization: 'Basic d2ViLWFwcDo=' },
      };

      formData.append('refresh_token', refreshTokenString);
      formData.append('grant_type', 'refresh_token');
      dispatch({
        type: ActionTypes.TOKEN_REFRESH_REQUEST,
      });
      dispatch({ type: ActionTypes.GET_AUTH_REQUEST });

      return fetch(url, options)
        .then(response => response.text())
        .then(response => {
          return response ? JSON.parse(response) : {};
        })
        .then(response => {
          if (response.access_token === '' || response.uid === '') {
            openLogoutOverlay(dispatch);
            return dispatch({
              type: ActionTypes.GET_AUTH_FAILURE,
              payload: { message: 'no data' },
            });
          }

          /**
           * If we have successfully refreshed the token,
           * then we should re-initate the token countdown
           * as if we had just logged in.
           */
          dispatch({
            type: ActionTypes.GET_AUTH_SUCCESS,
            payload: response,
          });

          // inform redux listeners that a refresh happened
          dispatch({
            type: ActionTypes.TOKEN_REFRESH_SUCCESS,
          });
        })
        .catch(error => {
          openLogoutOverlay(dispatch);
          dispatch({
            type: ActionTypes.TOKEN_REFRESH_FAILURE,
          });

          // do we need to return this?
          return dispatch({ type: ActionTypes.GET_AUTH_FAILURE, error });
        });
    }

    return Promise.resolve();
  };
};

export const resetUserPassword = form => {
  return dispatch => {
    dispatch({ type: ActionTypes.RESET_PASSWORD_REQUEST });

    const url = '/uaa/resetPassword';
    const body = JSON.stringify(form);
    const options = {
      mode: 'cors',
      method: 'POST',
      body,
      headers: {
        Authorization: 'Basic d2ViLWFwcDo=',
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };

    return dispatch(apiCall(url, options))
      .then(() => dispatch({ type: ActionTypes.RESET_PASSWORD_SUCCESS }))
      .catch(error => {
        dispatch({ type: ActionTypes.RESET_PASSWORD_FAILURE, error });
        return Promise.reject(error);
      });
  };
};

export const checkPasswordResetValidity = resetToken => {
  return dispatch => {
    dispatch({ type: ActionTypes.CHECK_PASSWORD_RESET_VALID_REQUEST });

    const url = `/uaa/resetPassword/${resetToken}`;

    return dispatch(apiCall(url))
      .then(() =>
        dispatch({ type: ActionTypes.CHECK_PASSWORD_RESET_VALID_SUCCESS }),
      )
      .catch(() =>
        dispatch({ type: ActionTypes.CHECK_PASSWORD_RESET_VALID_FAILURE }),
      );
  };
};

export const setNewUserPassword = (resetToken, newPassword) => {
  return dispatch => {
    dispatch({ type: ActionTypes.SET_NEW_PASSWORD_REQUEST });

    const url = `/uaa/resetPassword/${resetToken}`;
    const options = {
      method: 'POST',
      body: JSON.stringify({ newPassword }),
    };

    return dispatch(apiCall(url, options))
      .then(() => dispatch({ type: ActionTypes.SET_NEW_PASSWORD_SUCCESS }))
      .catch(error => {
        dispatch({ type: ActionTypes.SET_NEW_PASSWORD_FAILURE, error });
        return Promise.reject(error);
      });
  };
};

export const resendEmailConfirmation = token => {
  return dispatch => {
    dispatch({ type: ActionTypes.RESEND_ACCOUNT_CONFIRMATION_REQUEST });

    const url = `/uaa/resendConfirmationEmail/${token}`;
    const options = { method: 'POST' };

    return dispatch(apiCall(url, options))
      .then(() =>
        dispatch({ type: ActionTypes.RESEND_ACCOUNT_CONFIRMATION_SUCCESS }),
      )
      .catch(error =>
        dispatch({
          type: ActionTypes.RESEND_ACCOUNT_CONFIRMATION_FAILURE,
          error,
        }),
      );
  };
};
